import { Avatar, Tooltip, Typography, TooltipProps } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { CSSProperties, useState } from "react";
import type { ComponentProps } from "react";
import { CustomTooltip } from "./CustomTooltip";

const { Text } = Typography;

// Color wheel for avatars - all colors are dark enough for white text
const AVATAR_COLORS = [
  "#2F80ED", // Blue
  "#9B51E0", // Purple
  "#219653", // Green
  "#F2994A", // Orange
  "#EB5757", // Red
  "#5D6B98", // Gray Blue
  "#5C3EBC", // Deep Purple
  "#2E7D32", // Forest Green
  "#D63964", // Pink
  "#4E8098", // Steel Blue
];

// Standard sizes configuration
const AVATAR_SIZES = {
  small: {
    avatar: 24,
    fontSize: 12,
    nameSize: 12,
  },
  medium: {
    avatar: 32,
    fontSize: 14,
    nameSize: 14,
  },
  large: {
    avatar: 40,
    fontSize: 16,
    nameSize: 16,
  },
} as const;

type AvatarSize = keyof typeof AVATAR_SIZES;

function getColorFromString(str: string): string {
  // Get a consistent index from the string
  const index = [...str].reduce((acc, char) => {
    return acc + char.codePointAt(0)!;
  }, 0);

  // Use modulo to get a value within our color array length
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}

// Calculate font size based on avatar size
function getFontSize(size: number): number {
  // For single-digit sizes, use a minimum
  if (size < 10) return 8;
  // For typical avatar sizes, maintain a ratio
  return Math.floor(size * 0.4);
}

export interface UserData {
  uuid: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  role?: string;
}

export interface UserAvatarProps {
  user: UserData;
  size?: AvatarSize;
  showTooltip?: boolean;
  showName?: boolean;
  tooltipPlacement?: ComponentProps<typeof Tooltip>["placement"];
  className?: string;
  style?: CSSProperties;
  shape?: "circle" | "square";
  nameStyle?: CSSProperties;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = "small",
  showTooltip = true,
  showName = true,
  tooltipPlacement = "top",
  className,
  style,
  shape = "circle",
  nameStyle,
}) => {
  const [imgError, setImgError] = useState(false);
  const sizeConfig = AVATAR_SIZES[size];
  const initials = user.name
    ?.split(" ")
    ?.map((name: string) => name[0]?.toLocaleUpperCase())
    ?.join("");

  const handleError = () => {
    setImgError(true);
    return true; // Return true to prevent default error handling
  };

  const avatarElement = (
    <Avatar
      size={sizeConfig.avatar}
      shape={shape}
      src={imgError ? undefined : user.avatarUrl}
      onError={handleError}
      style={{
        backgroundColor:
          imgError || !user.avatarUrl
            ? getColorFromString(user.name)
            : undefined,
        color: imgError || !user.avatarUrl ? "#FFFFFF" : undefined,
        fontSize: imgError || !user.avatarUrl ? sizeConfig.fontSize : undefined,
        fontWeight: 500,
        ...style,
      }}
      className={className}
    >
      {imgError || !user.avatarUrl ? initials : null}
    </Avatar>
  );

  const content = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {avatarElement}
      {showName && (
        <Text style={{ fontSize: sizeConfig.nameSize, ...nameStyle }}>
          {user.name}
        </Text>
      )}
    </div>
  );

  if (showTooltip) {
    const tooltipContent = (
      <div>
        <div>{user.name}</div>
        {user.email && <div>{user.email}</div>}
        {user.role && <div>{user.role}</div>}
      </div>
    );

    return (
      <CustomTooltip content={tooltipContent} placement={tooltipPlacement}>
        {content}
      </CustomTooltip>
    );
  }

  return content;
};
